import React, { createContext, useContext, useState, useEffect } from 'react';
import { observer } from 'mobx-react';
import coursewareStore from '../../modules/courseware/coursewareStore';
import Loader from '../loader/Loader';

interface StandaloneModeContextType {
  isStandaloneMode: boolean;
  setStandaloneMode: (enabled: boolean) => void;
}

const StandaloneModeContext = createContext<StandaloneModeContextType>({
  isStandaloneMode: false,
  setStandaloneMode: () => {},
});

export const useStandaloneMode = () => useContext(StandaloneModeContext);

// Loader shown while waiting for courseware config to determine standalone mode
const StandaloneLoader: React.FC = () => (
  <div style={{
    width: '100%',
    height: '100vh',
    backgroundColor: 'rgba(255, 255, 255, 0.7)',
    position: 'fixed',
    zIndex: 999999,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  }}>
    <Loader style={{ position: 'fixed', top: '50%', left: '50%', height: 100, transform: 'translate(-50%, -50%)', zIndex: 2000 }} />
  </div>
);

const StandaloneModeProviderInner: React.FC<{ children: React.ReactNode }> = observer(({ children }) => {
  const [isStandaloneModeState, setIsStandaloneModeState] = useState(false);

  // Derive standalone mode from coursewareStore.requireAuthentication once config is loaded
  useEffect(() => {
    if (coursewareStore.configLoaded) {
      const standalone = coursewareStore.requireAuthentication;
      // console.log('[STANDALONE] Config loaded. requireAuthentication =', standalone, '→ standalone =', standalone);
      setIsStandaloneModeState(standalone);
      if (standalone) {
        sessionStorage.setItem('standaloneMode', 'true');
      } else {
        sessionStorage.removeItem('standaloneMode');
      }
    }
  }, [coursewareStore.configLoaded, coursewareStore.requireAuthentication]);

  const setStandaloneMode = (enabled: boolean) => {
    setIsStandaloneModeState(enabled);
    if (enabled) {
      sessionStorage.setItem('standaloneMode', 'true');
    } else {
      sessionStorage.removeItem('standaloneMode');
    }
  };

  // Show loader until courseware config is loaded — prevents any UI flash
  if (!coursewareStore.configLoaded) {
    return <StandaloneLoader />;
  }

  return (
    <StandaloneModeContext.Provider value={{ isStandaloneMode: isStandaloneModeState, setStandaloneMode }}>
      {children}
    </StandaloneModeContext.Provider>
  );
});

export const StandaloneModeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return <StandaloneModeProviderInner>{children}</StandaloneModeProviderInner>;
};
